declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export interface GA4UserProperties {
  user_type: "New" | "Returning";
  city: string | null;
  membership_type: "Free" | "Paid" | "Corporate" | null;
  industry: string | null;
}

export interface GA4EventParams {
  [key: string]: string | number | boolean | null | undefined;
}

export function initGA(measurementId: string): void {
  if (!measurementId) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "[Analytics] REACT_APP_GA4_MEASUREMENT_ID is not set. Analytics disabled.",
      );
    }
    return;
  }

  // Prevent double-injection on re-renders
  if (document.getElementById("ga4-script")) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", measurementId, { send_page_view: false });

  const script = document.createElement("script");
  script.id = "ga4-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
}

export function setUserId(userId: string | null): void {
  if (typeof window.gtag !== "function") return;
  window.gtag("set", { user_id: userId ?? undefined });
}

export function setUserProperties(properties: GA4UserProperties): void {
  if (typeof window.gtag !== "function") return;
  // 'set' persists properties for all subsequent events
  window.gtag("set", "user_properties", properties);
  // Also send with config to ensure GA4 registers them immediately
  window.gtag("config", process.env.REACT_APP_GA4_MEASUREMENT_ID ?? "", {
    user_properties: properties,
  });
}

export function trackPageView(path: string, title?: string): void {
  if (typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title ?? document.title,
  });
}

export function trackEvent(eventName: string, params?: GA4EventParams): void {
  if (typeof window.gtag !== "function") return;
  try {
    window.gtag("event", eventName, params ?? {});
  } catch {
    // Analytics must never crash the app
  }
}

// ─── Discovery Events ────────────────────────────────────────────────────────

/** Fired when the app is opened / user lands on the app for the first time */
export function trackAppOpen(source?: string, campaign?: string): void {
  trackEvent("app_open", {
    source: source ?? null,
    campaign: campaign ?? null,
  });
}

/** Fired when the Home / Dashboard screen is viewed */
export function trackHomeView(): void {
  trackEvent("home_view");
}

/** Fired when a tab/category is tapped (Workspace, Events, Wellness, Retreat…) */
export function trackCategoryClick(categoryName: string): void {
  trackEvent("category_click", { category_name: categoryName });
}

// ─── Browsing Behaviour ──────────────────────────────────────────────────────

/** Fired when a listing/slot list is viewed for a category */
export function trackListingView(categoryName: string): void {
  trackEvent("listing_view", { category_name: categoryName });
}

/** Fired when a specific slot/item card is tapped */
export function trackItemClick(itemId: string, itemName: string): void {
  trackEvent("item_click", { item_id: itemId, item_name: itemName });
}

/** Fired when a detail page is fully viewed */
export function trackDetailView(itemId: string, category: string): void {
  trackEvent("detail_view", { item_id: itemId, category });
}

// ─── Intent Signals ──────────────────────────────────────────────────────────

/** Fired when a time slot is selected before reserving */
export function trackSlotSelected(timeSlot: string, date: string): void {
  trackEvent("slot_selected", { time_slot: timeSlot, date });
}

/** Fired when the Reserve / Add-to-cart button is tapped */
export function trackReserveClick(itemId: string): void {
  trackEvent("reserve_click", { item_id: itemId });
}

// ─── Conversion Funnel ───────────────────────────────────────────────────────

/** Fired when the payment/checkout screen is opened */
export function trackCheckoutStarted(totalAmount: number): void {
  trackEvent("checkout_started", { total_amount: totalAmount });
}

/** Fired when Razorpay is opened (payment initiated) */
export function trackPaymentInitiated(paymentMethod: string): void {
  trackEvent("payment_initiated", { payment_method: paymentMethod });
}

/** Fired after payment is verified successfully */
export function trackPaymentSuccess(
  amount: number,
  bookingId: string,
  itemName: string,
  purchaseType: "membership" | "slot",
): void {
  trackEvent("payment_success", {
    amount,
    booking_id: bookingId,
    item_name: itemName,
    purchase_type: purchaseType,
    currency: "INR",
  });
}

/** Fired when payment fails or is cancelled */
export function trackPaymentFailed(reason: string): void {
  trackEvent("payment_failed", { reason });
}

// ─── Drop-off Indicators ─────────────────────────────────────────────────────

/** Fired when user abandons checkout (Razorpay modal dismissed) */
export function trackCheckoutAbandoned(step: string): void {
  trackEvent("checkout_abandoned", { step });
}

/** Fired when back button is tapped on a screen */
export function trackBackClicked(screenName: string): void {
  trackEvent("back_clicked", { screen_name: screenName });
}

// ─── Auth Events ─────────────────────────────────────────────────────────────

/** Fired after OTP is validated successfully */
export function trackLogin(): void {
  trackEvent("login", { method: "otp" });
}

/** Fired after profile setup is completed (new user) */
export function trackSignUp(): void {
  trackEvent("sign_up", { method: "otp" });
}

// ─── Membership Events ───────────────────────────────────────────────────────

/** Fired when "Become a Member" CTA is tapped */
export function trackMembershipApplied(): void {
  trackEvent("membership_applied");
}

// ─── Score Card ──────────────────────────────────────────────────────────────

/** Fired when score card result is shown */
export function trackScoreCardCompleted(readinessScore: number): void {
  trackEvent("score_card_completed", { readiness_score: readinessScore });
}
